/*
 * @Author: Ryan
 * @Date: 2026-01-19
 * @Description: 景深效果轮播组件 - 通用组件
 */
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import styles from './index.module.scss';

export interface DepthCarouselProps<T> {
  /** 轮播项列表 */
  items: T[];
  /** 渲染每个轮播项的函数 */
  renderItem: (item: T, index: number, isActive: boolean) => React.ReactNode;
  /** 自动轮播间隔（毫秒），默认 5000 */
  autoPlayInterval?: number;
  /** 是否自动播放，默认 true */
  autoPlay?: boolean;
  /** 轮播项宽度（PC），默认 680 */
  itemWidth?: number;
  /** 轮播项高度（PC），默认 400 */
  itemHeight?: number;
  /** 轮播项间距，默认 24 */
  itemGap?: number;
  /** 自定义类名 */
  className?: string;
}

function DepthCarousel<T extends { id: string }>({
  items,
  renderItem,
  autoPlayInterval = 5000,
  autoPlay = true,
  itemWidth = 680,
  itemHeight = 400,
  itemGap = 24,
  className,
}: DepthCarouselProps<T>) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  // 控制进度条：'reset' 表示重置到0，'running' 表示动画到100%
  const [progressState, setProgressState] = useState<'reset' | 'running'>('reset');
  const autoPlayTimerRef = useRef<NodeJS.Timeout | null>(null);

  const totalItems = items.length;

  // 清除自动播放定时器
  const clearAutoPlayTimer = useCallback(() => {
    if (autoPlayTimerRef.current) {
      clearTimeout(autoPlayTimerRef.current);
      autoPlayTimerRef.current = null;
    }
  }, []);

  // 开始自动播放（设置定时器 + 启动进度条动画）
  const startAutoPlay = useCallback(() => {
    if (!autoPlay || totalItems <= 1) return;

    // 先重置进度条
    setProgressState('reset');

    // 下一帧启动进度条动画
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setProgressState('running');
      });
    });

    // 设置自动切换定时器
    autoPlayTimerRef.current = setTimeout(() => {
      setActiveIndex((prev) => (prev + 1) % totalItems);
    }, autoPlayInterval);
  }, [autoPlay, autoPlayInterval, totalItems]);

  // 切换到指定索引
  const goToIndex = useCallback(
    (index: number) => {
      if (index === activeIndex) return;
      clearAutoPlayTimer();
      setActiveIndex(index);
    },
    [activeIndex, clearAutoPlayTimer],
  );

  // 切换到下一张
  const goToNext = useCallback(() => {
    clearAutoPlayTimer();
    setActiveIndex((prev) => (prev + 1) % totalItems);
  }, [totalItems, clearAutoPlayTimer]);

  // 切换到上一张
  const goToPrev = useCallback(() => {
    clearAutoPlayTimer();
    setActiveIndex((prev) => (prev - 1 + totalItems) % totalItems);
  }, [totalItems, clearAutoPlayTimer]);

  // activeIndex 变化时重新启动自动播放
  useEffect(() => {
    if (isPaused) return;
    startAutoPlay();
    return clearAutoPlayTimer;
  }, [activeIndex, isPaused, startAutoPlay, clearAutoPlayTimer]);

  // 鼠标悬停时暂停
  const handleMouseEnter = () => {
    setIsPaused(true);
    clearAutoPlayTimer();
    setProgressState('reset');
  };

  const handleMouseLeave = () => {
    setIsPaused(false);
  };

  // 计算每个项目的位置和样式
  const getItemStyle = (index: number): React.CSSProperties => {
    const diff = index - activeIndex;
    const normalizedDiff =
      ((diff + totalItems + Math.floor(totalItems / 2)) % totalItems) -
      Math.floor(totalItems / 2);

    const isActive = normalizedDiff === 0;
    const isAdjacent = Math.abs(normalizedDiff) === 1;
    const isSecondary = Math.abs(normalizedDiff) === 2;

    // 计算位移：基于卡片宽度和间距
    const offsetUnit = itemWidth + itemGap;

    let translateX = 0;
    let scale = 0.6;
    let opacity = 0;
    let zIndex = 0;
    let blur = 6;

    if (isActive) {
      translateX = 0;
      scale = 1;
      opacity = 1;
      zIndex = 30;
      blur = 0;
    } else if (isAdjacent) {
      translateX = normalizedDiff * offsetUnit * 0.85;
      scale = 0.75;
      opacity = 0.5;
      zIndex = 20;
      blur = 3;
    } else if (isSecondary) {
      translateX = normalizedDiff * offsetUnit * 1.4;
      scale = 0.6;
      opacity = 0.25;
      zIndex = 10;
      blur = 5;
    } else {
      translateX = normalizedDiff > 0 ? offsetUnit * 2 : -offsetUnit * 2;
      scale = 0.5;
      opacity = 0;
      zIndex = 0;
      blur = 8;
    }

    return {
      transform: `translateX(${translateX}px) scale(${scale})`,
      opacity,
      zIndex,
      filter: `blur(${blur}px)`,
      width: itemWidth,
      height: itemHeight,
    };
  };

  // 获取进度条样式
  const getProgressStyle = (index: number): React.CSSProperties => {
    const isActive = index === activeIndex;

    if (!isActive) {
      return { width: '0%', transition: 'none' };
    }

    if (progressState === 'reset') {
      return { width: '0%', transition: 'none' };
    }

    // running 状态：使用 CSS transition 动画到 100%
    return {
      width: '100%',
      transition: `width ${autoPlayInterval}ms linear`,
    };
  };

  return (
    <div
      className={`${styles.carouselContainer} ${className || ''}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* 轮播内容 */}
      <div className={styles.carouselTrack} style={{ height: itemHeight + 40 }}>
        {items.map((item, index) => (
          <div
            key={item.id}
            className={`${styles.carouselItem} ${index === activeIndex ? styles.active : ''}`}
            style={getItemStyle(index)}
            onClick={() => goToIndex(index)}
          >
            {renderItem(item, index, index === activeIndex)}
          </div>
        ))}
      </div>

      {/* 导航按钮 */}
      <div className={styles.navigation}>
        <Button
          type="default"
          shape="circle"
          icon={<LeftOutlined />}
          onClick={goToPrev}
          className={styles.navButton}
          aria-label="Previous slide"
        />
        <Button
          type="default"
          shape="circle"
          icon={<RightOutlined />}
          onClick={goToNext}
          className={styles.navButton}
          aria-label="Next slide"
        />
      </div>

      {/* 进度指示器 */}
      <div className={styles.indicators}>
        {items.map((_, index) => (
          <button
            key={index}
            className={`${styles.indicator} ${index === activeIndex ? styles.indicatorActive : ''}`}
            onClick={() => goToIndex(index)}
            aria-label={`Go to slide ${index + 1}`}
          >
            <div className={styles.indicatorProgress} style={getProgressStyle(index)} />
          </button>
        ))}
      </div>
    </div>
  );
}

export default DepthCarousel;
